/**
 * RFC 8628 device-flow consent — the browser half of agent pairing (A3).
 *
 * The agent runs headless on a farm PC with no browser, so it cannot redirect
 * anyone anywhere. Instead it displays a short `user_code` and a verification
 * URI; the farmer opens that URI **here**, logged in as themselves, types the
 * code, and approves. The tenant binding happens at that moment, on our side —
 * the agent learns which farm it belongs to as a *result* of authorization, and
 * never holds a tenant identifier it could have chosen itself.
 *
 * Session-cookie authenticated, like every other browser surface: these routes
 * take `Depends(get_current_user)`, and that session IS the tenant the client
 * gets bound to. The agent's own credentials never pass through the browser.
 */
import api from './api';

export interface DeviceVerification {
  /** The registry `clients` allowlist entry, e.g. `hofsync-agent`. Rendered to
   *  the farmer as the identity of the software asking — the allowlist is what
   *  stops this being a free-form phishing field. */
  software_id: string;
  provider: string;
  scope: string;
}

export interface ApprovedClient {
  client_id: string;
  provider: string;
  name: string | null;
}

/** `ABCD-EFGH` — grouped, upper-cased, non-alphanumerics dropped. */
export function normaliseUserCode(raw: string): string {
  const cleaned = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (cleaned.length <= 4) return cleaned;
  return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 8)}`;
}

export const devicePairingService = {
  /** What the consent screen renders. Consents to nothing. */
  verify: async (userCode: string): Promise<DeviceVerification> => {
    const response = await api.post<DeviceVerification>('/v1/oauth/device/verify', {
      user_code: userCode.toUpperCase(),
    });
    return response.data;
  },

  /** The consent moment — binds the client to this session's tenant. */
  approve: async (userCode: string, name?: string, platform?: string): Promise<ApprovedClient> => {
    const response = await api.post<ApprovedClient>('/v1/oauth/device/approve', {
      user_code: userCode.toUpperCase(),
      name: name || null,
      platform: platform || null,
    });
    return response.data;
  },

  deny: async (userCode: string): Promise<void> => {
    await api.post('/v1/oauth/device/deny', { user_code: userCode.toUpperCase() });
  },
};

/**
 * Disconnect a paired client — the v2 replacement for v1's
 * `DELETE /integrations/agent/agents/{id}` (S10).
 *
 * Revocation is immediate and stateful by design: machine tokens are verified
 * statelessly at the gateway, so nothing on the push path notices a revoke
 * until the token expires — the merge gate's `introspect_client` is what makes
 * it bite. Re-pairing afterwards is expected, which is why migration 041's
 * uniqueness is partial on `revoked_at IS NULL`.
 */
export async function revokeClient(clientId: string): Promise<void> {
  await api.delete(`/v1/oauth/clients/${clientId}`);
}
